/**
 * Task #3818 — Connettori del workspace PA: perimetro effettivo + restrizione
 * per-conversazione.
 *
 * Un "connettore" è un raggruppamento visibile dei tool MCP dell'agente PA:
 * - `internal:<category>`  → dominio interno EasyFlow (server MCP interno
 *   globale, raggruppato per categoria: crm, telephony, calendar, …);
 * - `mcp:<server_uuid>`    → server MCP esterno del tenant (uno per server).
 *
 * MODELLO DI AUTORITÀ (vincolante, tre livelli mai confusi):
 * 1. Admin = autorità: il perimetro effettivo deriva da mcp_tool_permissions +
 *    modalità delega/intersezione (task Delega consapevole). Nulla qui lo espande.
 * 2. Sidebar = visibilità: l'utente vede SOLO il perimetro effettivo.
 * 3. Per-chat = solo restrizione: `enabled` è sempre un SOTTOINSIEME del
 *    perimetro; `null` = default, tutto il perimetro attivo (nessun cambiamento).
 */
import { z } from 'zod';

/** Id connettore: `internal:<category>` oppure `mcp:<uuid>`. */
export const PA_CONNECTOR_ID_REGEX = /^(internal:[a-z][a-z0-9_]{0,63}|mcp:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})$/;

export const PaConnectorIdSchema = z.string().regex(PA_CONNECTOR_ID_REGEX, 'Id connettore non valido (atteso internal:<categoria> o mcp:<uuid>)');
export type PaConnectorId = z.infer<typeof PaConnectorIdSchema>;

export const PaConnectorKindSchema = z.enum(['internal', 'mcp']);
export type PaConnectorKind = z.infer<typeof PaConnectorKindSchema>;

/** Stato di salute normalizzato lato backend (mcp_server_configs.health_status). */
export const PaConnectorHealthSchema = z.enum(['healthy', 'unhealthy', 'unknown']);
export type PaConnectorHealth = z.infer<typeof PaConnectorHealthSchema>;

/** Modalità di delega dell'assistente (task Delega consapevole). */
export const PaConnectorsModeSchema = z.enum(['delegated', 'intersection']);
export type PaConnectorsMode = z.infer<typeof PaConnectorsModeSchema>;

export const PaConnectorToolSchema = z.object({
  name: z.string(),
  description: z.string().nullable(),
});
export type PaConnectorTool = z.infer<typeof PaConnectorToolSchema>;

export const PaConnectorSchema = z.object({
  id: PaConnectorIdSchema,
  kind: PaConnectorKindSchema,
  label: z.string(),
  description: z.string().nullable(),
  health: PaConnectorHealthSchema,
  tools: z.array(PaConnectorToolSchema),
});
export type PaConnector = z.infer<typeof PaConnectorSchema>;

/** GET /personal-assistant/workspace/connectors?assistantId=… */
export const PaConnectorsPerimeterResponseSchema = z.object({
  connectors: z.array(PaConnectorSchema),
  mode: PaConnectorsModeSchema,
});
export type PaConnectorsPerimeterResponse = z.infer<typeof PaConnectorsPerimeterResponseSchema>;

/**
 * GET /personal-assistant/workspace/connectors/conversations/:conversationId
 * `enabled === null` → nessuna restrizione (tutto il perimetro attivo).
 * Quando non-null è GIÀ intersecato col perimetro corrente (le revoche admin
 * spariscono automaticamente dalle selezioni esistenti).
 */
export const PaConversationConnectorsResponseSchema = z.object({
  enabled: z.array(PaConnectorIdSchema).nullable(),
});
export type PaConversationConnectorsResponse = z.infer<typeof PaConversationConnectorsResponseSchema>;

/**
 * PUT /personal-assistant/workspace/connectors/conversations/:conversationId
 * `enabled: null` rimuove la restrizione; array (anche vuoto) = restrizione.
 * Il server RIFIUTA (403) qualsiasi id fuori dal perimetro effettivo.
 */
export const PaConversationConnectorsSetRequestSchema = z.object({
  enabled: z.array(PaConnectorIdSchema).max(200).nullable(),
});
export type PaConversationConnectorsSetRequest = z.infer<typeof PaConversationConnectorsSetRequestSchema>;

/** Etichette umane dei domini interni (id `internal:<categoria>`). */
export const PA_INTERNAL_CONNECTOR_LABELS: Readonly<Record<string, string>> = {
  telephony: 'Telefonia',
  crm: 'CRM',
  hospitality: 'Hospitality',
  workflow: 'Workflow',
  workflow_control: 'Controllo workflow',
  calendar: 'Calendario',
  messaging: 'Messaggistica',
  campaign: 'Campagne',
  ai: 'AI',
  knowledge: 'Knowledge base',
  google: 'Google Workspace',
  web: 'Ricerca web',
  file_manager: 'File manager',
  video_call: 'Videochiamate',
  supplychain: 'Supply chain',
  hr: 'Risorse umane',
  wiki_read: 'Wiki (lettura)',
  wiki_write: 'Wiki (scrittura)',
  telegram: 'Telegram',
};

/** Etichetta di fallback per categorie interne non mappate. */
export function paInternalConnectorLabel(category: string): string {
  return PA_INTERNAL_CONNECTOR_LABELS[category] ?? category.replace(/_/g, ' ');
}
